// File: app/src/utils/capabilityPrompt.js
//
// Formats the user's tested performance capabilities (issue #18) into the
// optional "## Performance Capabilities" block injected into the LLM
// workout-generation prompt (see buildPrompt in app/src/apis/llmApi.js).
//
// Kept out of llmApi.js so it can be unit-tested without pulling in the
// provider SDK import chain.

import { secondsToClock } from "./clockInput";
import { benchmarkAge, estimateOneRm, normaliseEndurance } from "./benchmarkNormalise";
import { combineBenchmarks, repCurveLean } from "./capabilityCurves";

// Render a capability value for the prompt. Values are discrete numbers; time
// (unit "sec") is expanded to "<n> seconds (M:SS)" so the model reads pacing
// unambiguously rather than the terse stored "sec".
export const formatCapabilityValueForPrompt = (cap) => {
  if (cap.unit === "sec") {
    const clock = secondsToClock(cap.value, { tenths: true });
    return clock ? `${cap.value} seconds (${clock})` : `${cap.value} seconds`;
  }
  return cap.unit ? `${cap.value} ${cap.unit}` : `${cap.value}`;
};

// Build the optional "## Performance Capabilities" prompt block. Returns "" when
// there are none, so the prompt is byte-for-byte identical to the legacy prompt
// for users who have logged nothing.
const hasValue = (c) => c && c.value != null && c.value !== "";
const tempoSeconds = (c) => {
  const n = Number(c?.sec_per_rep);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// What the result was for (issue #88): "× 5 reps", "for 2000 m", "in 1:05".
const formatTarget = (c) => {
  const n = Number(c.target_value);
  if (!c.target_unit || !Number.isFinite(n) || n <= 0) return "";
  if (c.target_unit === "reps") return ` × ${n} reps`;
  if (c.target_unit === "sec") return ` in ${secondsToClock(n, { tenths: true })}`;
  return ` for ${n} ${c.target_unit}`;
};

// Khronos's normalised reading of the row, after an arrow.
const formatNormalised = (c) => {
  const oneRm = estimateOneRm(c);
  if (oneRm && !oneRm.tested) {
    return ` → ≈ ${oneRm.kg} kg estimated 1RM${oneRm.rough ? " (rough)" : ""}`;
  }
  const endurance = normaliseEndurance(c);
  if (endurance?.kind === "pace") {
    const eq = endurance.equivalents.map((e) => `≈ ${e.distance} ${e.time}`);
    return ` → ${endurance.pace} ${endurance.per} pace${eq.length ? `; ${eq.join(", ")}` : ""}`;
  }
  if (endurance?.kind === "rate") return ` → ≈ ${endurance.calPerMin} cal/min`;
  return "";
};

const formatStale = (c, now) => {
  const age = benchmarkAge(c, now);
  if (!age?.stale) return "";
  const months = Math.floor(age.days / 30.4);
  return ` — ${months} months old, may be out of date`;
};

// One line per activity where several rows were combined (issue #91).
const formatCombined = (s) => {
  if (s.kind === "lift") {
    const notes = [`combined from ${s.rows} results`];
    if (s.rough) notes.push("rough");
    if (s.stale) notes.push("over six months old");
    if (s.conflict) notes.push(`they disagree, ${s.conflict.low}–${s.conflict.high} kg; lean on the lower`);
    // How reps fall off with load matters for sizing sets of 8+ (issue #91).
    if (s.curve) notes.push(`rep curve fitted to their tested max: ${repCurveLean(s.curve.k)}`);
    return `- ${s.label}: ≈ ${s.kg} kg estimated 1RM (${notes.join("; ")})`;
  }
  const t = s.threshold;
  if (s.kind === "calories") {
    const notes = [`critical power from ${t.efforts} efforts`, `≈ ${t.reserve} cal in reserve above it`];
    if (t.rough) notes.push("rough");
    if (t.stale) notes.push("partly over six months old");
    const sprint = s.sprint ? `, sprint ≈ ${s.sprint.calPerMin} cal/min` : "";
    return `- ${s.label}: threshold ≈ ${t.calPerMin} cal/min (${notes.join("; ")})${sprint}`;
  }
  const notes = [`critical speed from ${t.efforts} efforts`, `≈ ${t.reserveM} m in reserve above it`];
  if (t.rough) notes.push("rough");
  if (t.stale) notes.push("partly over six months old");
  const sprint = s.sprint ? `, sprint ≈ ${s.sprint.pace} ${s.per}` : "";
  return `- ${s.label}: threshold ≈ ${t.pace} ${s.per} (${notes.join("; ")})${sprint}`;
};

const formatCombinedBlock = (capabilities, now) => {
  const combined = combineBenchmarks(capabilities, now);
  if (combined.length === 0) return "";
  return `
Combined per activity, reconciling the rows above. Prefer these where they apply: size threshold and interval work from the threshold pace, and loads from the combined 1RM:
${combined.map(formatCombined).join("\n")}
`;
};

export const buildCapabilitiesSection = (capabilities, now = new Date()) => {
  if (!Array.isArray(capabilities) || capabilities.length === 0) return "";
  const lines = capabilities
    .filter((c) => c && c.label != null && (hasValue(c) || tempoSeconds(c)))
    .map((c) => {
      const metric = c.metric ? ` — ${c.metric}` : "";
      const value = hasValue(c) ? `: ${formatCapabilityValueForPrompt(c)}` : "";
      const tempo = tempoSeconds(c);
      const tempoClause = tempo ? ` (~${tempo}s/rep tempo)` : "";
      const effort = c.effort ? ` (${c.effort})` : "";
      const category = c.category ? ` [${c.category}]` : "";
      const target = hasValue(c) ? formatTarget(c) : "";
      return `- ${c.label}${metric}${value}${target}${tempoClause}${effort}${category}${formatNormalised(c)}${formatStale(c, now)}`;
    });
  if (lines.length === 0) return "";
  return `
## Performance Capabilities
The user has logged these tested benchmarks. Use them to prescribe realistic, individualised loads and paces, and scale relative effort by any stated RPE/effort. After an arrow is Khronos's normalised figure: an estimated 1RM worked out from load, reps and effort (prescribe %1RM from it), or pace and race-time equivalents from a timed distance. Treat "(rough)" estimates and results marked out of date conservatively:
${lines.join("\n")}
${formatCombinedBlock(capabilities, now)}
When a prescribed exercise corresponds to one of these benchmarks, annotate its \`intensityTarget\` with the concrete figure in square brackets, e.g. \`75% 1RM [~100kg]\` or \`2k pace [7:30]\`.
`;
};
